import { useContext, useEffect, useState, useRef } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import Footer from 'comps/Footer';
import Header from 'comps/Header';
import Layout from 'comps/Layout';
import { SiteContext, UserContext } from 'libs/contexts';
import * as UserService from 'api/user';
import ProductService, { IProductDetailParams } from 'api/product';
import ProductFormDeatilComp from '../conponents/ProductFormDeatil';
import CategoryBreadcrumb, { Category } from 'comps/CategoryBreadcrumb';

import useAreaCookie from 'hooks/useAreaCookie';
import useLocalPayment from 'hooks/useLocalPayment';
import type { IProductDetail, IPriceConfig } from '../product';
import {
  NetPriceTypeContextProvider,
  TradeModeContextProvider,
  ProductDetailContextProvider,
  TradeAgreementContextProvider,
  ShopDataContextProvider,
  ForwardRefContextProvider,
} from '../context';
import SideBar from 'comps/SideBar';
import ShopInfo from '../conponents/ShopInfo';
import { isCXZQProduct } from '../helper';
import ConcreteProductList from './ConcreteProductList';
import { ProductDescriptWithRecommend } from './ProductDescriptWithRecommend';
import { Spin } from 'antd';
import styles from './index.module.scss';

type IProps = {
  categories: Category[];
  productDetailData: IProductDetail;
  shopData: any;
};

let detailCount = 0; // 重复多次调用场景下 获取请求最新结果
let chooseAgreement = null; // 选择交易协议
const ProductDetail = ({ categories, productDetailData, shopData }: IProps) => {
  const site = useContext(SiteContext);
  const route = useRouter();
  const id = route?.query?.id as string;
  // NOTE: 处理再次购买场景
  const orderSysNo = route?.query?.orderSysNo as string;
  const orderType = route?.query?.orderType as string;
  const [productDetail, setProductDetail] = useState(productDetailData as IProductDetail);
  const { getCookieAreaSysNo } = useAreaCookie();
  const [priceConfig, setPriceConfig] = useState({} as IPriceConfig);
  // NOTE: 获取默认的交易方式
  const { getFrontDefaultTradeMethodList } = useLocalPayment();
  const userInfo = useContext(UserContext);
  // 速采清单
  const productAttrListCompRef = useRef();
  // 商品清单
  const productMmuListCompRef = useRef();
  const [showLoading, setShowLoading] = useState(false);

  const getPriceConfig = async () => {
    const { data } = await UserService.showGoodsPricePermission();
    setPriceConfig(data);
  };
  const getProductDetail = async (param = { chooseAgreement: null }, isRefreshAttrList = false) => {
    try {
      detailCount += 1;
      if (param.chooseAgreement) {
        chooseAgreement = param.chooseAgreement;
      }
      setShowLoading(true);
      const _count = detailCount;
      // 商品详情查询参数
      const frontDefaultTradeMethodList = getFrontDefaultTradeMethodList();

      // TODO: mmuAttributeTagList
      const params: IProductDetailParams = {
        sysNo: productDetail.sysNo || id,
        areaSysNo: getCookieAreaSysNo(),
        frontDefaultTradeMethodList,
        mmuAttributeList: productDetail.mmuAttributeList || [],
        netPriceType: productDetail.netPriceType || param.netPriceType,
        chooseAgreement,
        ...param,
      };

      const { data } = await ProductService.getProductDetail(params);
      setShowLoading(false);
      if (isCXZQProduct(shopData)) {
        // 创新专区店铺特殊处理，不显示价格 @张政需求
        data.showPrice = false;
      }
      // 防止重复请求，导致数据混乱
      if (_count === detailCount) {
        setProductDetail(data as IProductDetail);
      }
      setTimeout(() => {
        if (isRefreshAttrList) {
          data.useSkuList ? getMmuSkuList() : getProductSkuList();
        }
      }, 30);
    } catch (error) {}
  };

  const getProductSkuList = () => {
    if (productAttrListCompRef?.current) {
      productAttrListCompRef.current.getProductSkuList();
    }
  };

  const getMmuSkuList = (sku?: any, sysNo?: string, type?: string) => {
    if (productMmuListCompRef?.current) {
      productMmuListCompRef.current.getSkuList(sku, sysNo, type);
    }
  };

  // [普通商品]速采清单
  useEffect(() => {
    !productDetail.useSkuList && getProductSkuList();
    getPriceConfig();
  }, []);

  // 混凝土清单，手动
  useEffect(() => {
    orderSysNo && orderType && getMmuSkuList(null, orderSysNo, orderType);
  }, [orderSysNo, orderType]);

  return (
    <>
      {showLoading && (
        <div className={styles.pageLoading}>
          <Spin size="large" />
        </div>
      )}
      <Layout
        header={<Header />}
        footer={<Footer />}
        keywords="数字化,供应链管理,数字采购,供应链金融"
        description={`${
          site?.basic?.tenantName || ''
        }通过数字化供应链管理、数字采购、供应链金融等产品和服务，实现产业链上下游协作，进而降本增效`}
        showFixedMenu
        bgColor="#ffffff"
        supplierSysNo={shopData?.supplierSysNo}
      >
        {productDetail?.supplierLimitEnable && (
          <div className={styles['trade-constraint-tip']}>
            该供应商已被限制平台交易，您可以继续浏览店铺或商品，但不能向供应商下单。
          </div>
        )}
        <div className={styles.product_container}>
          <div className={styles.breadcrumb}>
            <CategoryBreadcrumb
              data={categories}
              treeProps={{
                sysNo: 'id',
                cascadeSysNo: 'cascadeId',
                name: 'title',
              }}
              categorySysNoList={productDetail?.categorySysNoList}
              isToProductList={true}
            >
              <Link href="/">
                <a title="首页" className={styles.breadcrumb_item}>
                  首页
                </a>
              </Link>
            </CategoryBreadcrumb>
            <ShopInfo shopData={shopData} />
          </div>

          <ProductDetailContextProvider
            getProductDetail={getProductDetail}
            getMmuSkuList={getMmuSkuList}
            setShowLoading={setShowLoading}
          >
            <TradeAgreementContextProvider skuDetail={productDetail}>
              <NetPriceTypeContextProvider skuDetail={productDetail}>
                <TradeModeContextProvider>
                  <ShopDataContextProvider shop={shopData}>
                    <ProductFormDeatilComp info={productDetail} shopId={shopData?.sysNo} />
                    <ConcreteProductList
                      productDetail={productDetail}
                      shopData={shopData}
                      ref={productMmuListCompRef}
                    />
                    <ForwardRefContextProvider value={productAttrListCompRef}>
                      <ProductDescriptWithRecommend
                        productDetail={productDetail}
                        priceConfig={priceConfig}
                      />
                    </ForwardRefContextProvider>
                  </ShopDataContextProvider>
                </TradeModeContextProvider>
              </NetPriceTypeContextProvider>
            </TradeAgreementContextProvider>
          </ProductDetailContextProvider>
          {!!userInfo?.isPurchaser && <SideBar />}
        </div>
      </Layout>
    </>
  );
};

export default ProductDetail;
